import { UseAutocompleteProps } from '@mui/base/useAutocomplete/useAutocomplete';
import {
  Autocomplete,
  AutocompleteChangeReason,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import { FilterKeys, GroupSubject, GroupType } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import {
  SelectOptionsArchivedEmptyStub,
  SelectOptionsArchivedIcon,
} from '@schooly/components/filters';
import { GROUP } from '@schooly/constants/test-ids';
import { ArchiveIcon, DropdownIcon, Skeleton } from '@schooly/style';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { FormTextField } from '../../../../components/uikit-components/FormTextField/FormTextField';
import { InputEndAdornment } from '../../../../components/uikit-components/FormTextField/InputEndAdornment';
import { useAddGroupModal } from '../useAddGroupModal';

export interface SubjectSelectProps {
  isEditingLocked?: boolean;
}

type MuiAutocompleteValue =
  | {
      label: string;
      value: string;
    }
  | null
  | undefined;

const subjectToOptionValue = (subject?: GroupSubject) =>
  subject?.id ? { label: subject.name, value: subject.id } : undefined;

export const SubjectSelect: FC<SubjectSelectProps> = ({ isEditingLocked }) => {
  const { $t } = useIntl();
  const { subject, groupSubjects, subjectsLoading, focusedField, actions } = useAddGroupModal({});
  const { permissions } = useAuth();
  const isAdmin = permissions?.includes('school_admin');
  const { getConfirmation } = useConfirmationDialog();

  const groupSubjectsOptions = useMemo(
    () => groupSubjects.map(subjectToOptionValue),
    [groupSubjects],
  );

  const hasOptions = Boolean(groupSubjectsOptions.length);
  const noOptionsNoneSelected = !hasOptions && !subject?.id;

  const isSubjectOptionEqualToValue = useCallback<
    Exclude<
      UseAutocompleteProps<
        { label: string; value: string } | undefined,
        undefined,
        undefined,
        undefined
      >['isOptionEqualToValue'],
      undefined
    >
  >((option, value) => {
    return option?.value === value?.value;
  }, []);

  const autocompleteInputRef = useRef<HTMLInputElement>();
  const blurAutocompleteInputOnClear = useCallback<
    Exclude<
      UseAutocompleteProps<
        { label: string; value: string } | undefined,
        undefined,
        undefined,
        undefined
      >['onInputChange'],
      undefined
    >
  >((event, value, reason) => {
    if (reason === 'clear') {
      setTimeout(() => autocompleteInputRef.current?.blur());
    }
  }, []);

  const handleChange = useCallback(
    async (
      event: React.SyntheticEvent,
      option: MuiAutocompleteValue,
      reason: AutocompleteChangeReason,
    ) => {
      if (reason === 'clear' && subject?.archived) {
        const confirmed = await getConfirmation({
          textId: `deselect-subject-archived`,
          textValues: { name: subject?.name },
        });
        if (!confirmed) {
          autocompleteInputRef.current?.blur();
          return;
        }
      }
      return actions.setContextState({
        group_type: GroupType.Subject,
        subject: {
          id: option?.value!,
          name: option?.label!,
        },
      });
    },
    [actions, getConfirmation, subject?.archived, subject?.name],
  );

  const endAdornment = useMemo(() => {
    if (isEditingLocked) {
      return <InputEndAdornment locked />;
    }
    if (noOptionsNoneSelected) {
      return (
        <InputEndAdornment
          required
          customIcon={<SelectOptionsArchivedIcon isAdmin={isAdmin} type={FilterKeys.Subject} />}
        />
      );
    }
    return <InputEndAdornment customIcon={<DropdownIcon />} required />;
  }, [isAdmin, isEditingLocked, noOptionsNoneSelected]);

  return (
    <Stack mt={-2}>
      {!subjectsLoading ? (
        <Autocomplete
          key={subject?.id}
          options={groupSubjectsOptions}
          value={subjectToOptionValue(subject ?? undefined)}
          isOptionEqualToValue={isSubjectOptionEqualToValue}
          openOnFocus
          blurOnSelect
          disableClearable={!subject?.id}
          disabled={isEditingLocked || noOptionsNoneSelected}
          onChange={handleChange}
          onInputChange={blurAutocompleteInputOnClear}
          fullWidth
          sx={{
            ' .MuiFormLabel-root.Mui-disabled': {
              color: 'text.secondary',
            },
          }}
          PaperComponent={
            !hasOptions
              ? (props) => (
                  <Paper elevation={0} {...props} sx={{ px: 1, py: 1 }}>
                    <SelectOptionsArchivedEmptyStub isAdmin={isAdmin} type={FilterKeys.Subject} />
                  </Paper>
                )
              : undefined
          }
          renderInput={(params) => (
            <FormTextField
              {...params}
              sx={{
                '.MuiOutlinedInput-root:hover .MuiInputAdornment-root.MuiInputAdornment-positionEnd':
                  {
                    color: 'text.secondary',
                  },
                '.Mui-disabled .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'common.light3',
                },
              }}
              label={$t({ id: 'groups-GroupSubject' })}
              autoFocus={focusedField === 'subject'}
              fullWidth
              locked={isEditingLocked}
              startAdornment={
                subject?.archived && (
                  <InputAdornment position="start" sx={{ '&&': { mt: 0 } }}>
                    <Tooltip title={$t({ id: 'schoolProperty-Archived-subject' })}>
                      <IconButton inverse>
                        <ArchiveIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }
              endAdornment={endAdornment}
              showEndAdornment={!subject?.id || isEditingLocked || noOptionsNoneSelected}
              dropdown
              inputRef={autocompleteInputRef}
              data-test-id={GROUP.SUBJECT_DROPDOWN}
            />
          )}
        />
      ) : (
        <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} height={44} />
      )}
    </Stack>
  );
};
